// AdminEditor.jsx – Admin-Bearbeitungsansicht eines Stammbaumes
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TreeCanvas } from '../components/TreeCanvas';
import { AddPersonModal } from '../components/AddPersonModal';
import { PersonDetailPanel } from '../components/PersonDetailPanel';
import { PhotoEditor } from '../components/PhotoEditor';
import { CardPreview } from '../components/CardPreview';
import { renderEditedDataUrl, splitName } from '../utils/photoEdit';
import { renderCardCanvas, canvasToBlob, CARD_CM, CARD_DPI, CARD_PX } from '../utils/cardRender';
import { renderExtraCardCanvas } from '../utils/extraCardRender';
import { KATEGORIEN } from '../utils/extraCards';
import { orderPersonsForCards, extraPlanFor } from '../utils/cardOrder';
import { computeColorSystem } from '../utils/colorSystem';
import { LimitModal } from '../components/LimitModal';
import { simulateAdd, simulateEdit, checkChange } from '../utils/treeRules';
import { getColorLevels } from '../utils/colorSystem';
import { useAdminTreeData } from '../hooks/useAdminTreeData';
import { ReturnDialog } from './ReturnDialog';
import './Admin.css';

const AUTOSAVE_MS = 30000;

export default function AdminEditor({ api, token, tree, onBack, onLogout }) {
  const {
    persons, relationships, customColors,
    addPerson, updatePerson, deletePerson, setCustomColor,
    loadData, getSnapshot,
  } = useAdminTreeData();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [treeStatus, setTreeStatus] = useState(tree.status);
  const [treeEmail, setTreeEmail] = useState(tree.email || '');
  const [orderData, setOrderData] = useState(null);
  const [showRemark, setShowRemark] = useState(false);

  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editPersonId, setEditPersonId] = useState(null);
  const [presetRelation, setPresetRelation] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(null); // null | 'generations' | 'topGeneration'

  const [saveStatus, setSaveStatus] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const isDirtyRef = useRef(false);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Baum laden ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/api/admin/tree/load`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ treeCode: tree.code }),
        });
        if (res.status === 401) { onLogout(); return; }
        const d = await res.json();
        if (!res.ok) { if (!cancelled) setLoadError(d.error || 'Fehler'); return; }
        if (!cancelled) {
          loadData(d.data || {});
          setTreeEmail(d.email || '');
          setOrderData(d.orderData || null);
          setTreeStatus(d.status || 'in_bearbeitung');
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoadError('Server nicht erreichbar');
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Änderungen markieren
  useEffect(() => {
    if (!loading) isDirtyRef.current = true;
  }, [persons, relationships, customColors]); // eslint-disable-line

  // ── Auto-Save ─────────────────────────────────────────────────────────
  const doSave = useCallback(async (manual = false) => {
    if (!isDirtyRef.current) {
      if (manual) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      }
      return;
    }
    setSaveStatus('saving');
    try {
      const snap = getSnapshot();
      const res = await fetch(`${api}/api/admin/tree/save`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ treeCode: tree.code, data: snap }),
      });
      if (res.status === 401) { onLogout(); return; }
      if (res.ok) {
        isDirtyRef.current = false;
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  }, [api, tree.code, authHeaders, getSnapshot, onLogout]);

  useEffect(() => {
    if (loading) return;
    const iv = setInterval(doSave, AUTOSAVE_MS);
    return () => clearInterval(iv);
  }, [loading, doSave]);

  // ── Fertigstellen ─────────────────────────────────────────────────────
  const handleFinalize = async () => {
    if (!window.confirm('Stammbaum als "Fertiggestellt" markieren und Kunden-Mail senden?')) return;
    setFinalizing(true); setFinalizeError('');
    // Zuerst speichern
    await doSave();
    try {
      const res = await fetch(`${api}/api/admin/tree/finalize`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ treeCode: tree.code }),
      });
      const d = await res.json();
      if (!res.ok) { setFinalizeError(d.error || 'Fehler'); return; }
      setTreeStatus('fertiggestellt');
    } catch {
      setFinalizeError('Server nicht erreichbar');
    } finally {
      setFinalizing(false);
    }
  };

  // ── Person hinzufügen ─────────────────────────────────────────────────
  const handleAddSave = useCallback((formData, relationsData) => {
    const sim = simulateAdd(persons, relationships, formData, relationsData);
    const violation = checkChange(persons, relationships, sim.persons, sim.relationships);
    if (violation) { setShowLimitModal(violation); return; }
    addPerson(formData, relationsData);
    setShowAddModal(false);
    setPresetRelation(null);
  }, [addPerson, persons, relationships]);

  const handleEditSave = useCallback((formData, relationsData) => {
    const sim = simulateEdit(persons, relationships, editPersonId, formData, relationsData);
    const violation = checkChange(persons, relationships, sim.persons, sim.relationships);
    if (violation) { setShowLimitModal(violation); return; }
    updatePerson(editPersonId, formData, relationsData);
    setEditPersonId(null);
  }, [updatePerson, editPersonId, persons, relationships]);

  const handleQuickAdd = useCallback((personId, opt) => {
    setPresetRelation({ personId, opt });
    setShowAddModal(true);
  }, []);

  const handleColorChange = useCallback((personId, colorIndex) => {
    const gen1siblings = getColorLevels(persons, relationships)?.gen1siblings || [];
    setCustomColor(personId, colorIndex, gen1siblings);
  }, [persons, relationships, setCustomColor]);

  // ── Alle Fotos herunterladen (ZIP) ──────────────────────────────────
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);
  const [photoEditorState, setPhotoEditorState] = useState(null);
  const [showCardPreview, setShowCardPreview] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showRetouchList, setShowRetouchList] = useState(false);
  const retouchPersons = persons.filter(p => p.photo && p.photoRetouch);

  // Dateiname: Geburtsdatum_Nachname_Vorname (eindeutig)
  const makeNamer = () => {
    const used = new Map();
    const formatDate = (dateStr) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || '');
      return m ? `${m[1]}${m[2]}${m[3]}` : '00000000';
    };
    const safePart = (s) => (s || '').replace(/[^\p{L}\p{N}-]/gu, '');
    return (person) => {
      const n = splitName(person);
      const nachname = n.last || n.first || person.id;
      const vorname  = n.last ? n.first.replace(/\s+/g, '_') : '';
      const base = `${formatDate(person.birthDate)}_${safePart(nachname)}${vorname ? '_' + safePart(vorname) : ''}`;
      let name = base, i = 2;
      while (used.has(name)) name = `${base}_${i++}`;
      used.set(name, true);
      return name;
    };
  };
  const saveZip = async (zip, filename) => {
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // ── Alle Originalfotos herunterladen (ZIP) ──────────────────────────
  const handleDownloadAllPhotos = useCallback(async () => {
    const withPhoto = persons.filter(p => p.photo);
    if (withPhoto.length === 0) { alert('Keine Personen mit Foto vorhanden.'); return; }
    setDownloadingPhotos(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const nameFor = makeNamer();
      const retouchLines = [];
      for (const person of withPhoto) {
        const finalName = nameFor(person);
        const m = /^data:image\/(\w+);base64,(.*)$/.exec(person.photo || '');
        if (!m) continue;
        const ext = m[1] === 'jpeg' ? 'jpg' : m[1];
        // Originalfoto, so wie es hochgeladen wurde
        zip.file(`${finalName}.${ext}`, m[2], { base64: true });
        // Bild soll von Hand bearbeitet werden → zusätzlich in eigenen Ordner, mit Zuschnitt des Kunden
        if (person.photoRetouch) {
          zip.file(`Handbearbeitung/${finalName}_original.${ext}`, m[2], { base64: true });
          const cut = await renderEditedDataUrl(person.photo, person.photoEdit, 4000, 0.95);
          zip.file(`Handbearbeitung/${finalName}_zuschnitt_kunde.jpg`, cut.split(',')[1], { base64: true });
          retouchLines.push(`${finalName}  (${person.name || ''})\r\n   Wunsch: ${person.photoRetouch.note || '–'}\r\n`);
        }
      }
      if (retouchLines.length) {
        zip.file('Handbearbeitung/Wuensche.txt', `Bildbearbeitung von Hand – ${retouchLines.length} Bild(er) à 5 CHF\r\n\r\n${retouchLines.join('\r\n')}`);
      }
      await saveZip(zip, `fotos_${tree.code}.zip`);
    } catch (err) {
      console.error(err);
      alert('Fehler beim Erstellen der ZIP-Datei.');
    } finally {
      setDownloadingPhotos(false);
    }
  }, [persons, tree.code]); // eslint-disable-line

  // ── Fertige Memo-Karten herunterladen (Foto- und Namenskarten als PNG) ─
  const [downloadingCards, setDownloadingCards] = useState(null); // null | Fortschritt "3/20"
  const handleDownloadCards = useCallback(async () => {
    if (!persons.length) return;
    setDownloadingCards('0/' + persons.length);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const colors = computeColorSystem(persons, relationships, customColors || {});
      // gleiche Reihenfolge wie in der Vorschau: mehrfarbige Karten, dann jede Familie nach Alter
      const { ordered, familyColors } = orderPersonsForCards(persons, relationships, colors);
      const nameFor = makeNamer();
      const missing = [];
      const list = [];
      for (let i = 0; i < ordered.length; i++) {
        const person = ordered[i];
        const nr = String(i + 1).padStart(2, '0');
        const base = `${nr}_${nameFor(person)}`;
        const ci = colors.get(person.id);
        const front = await renderCardCanvas(person, ci, 'front');
        zip.file(`Fotokarten/${base}_foto.png`, await canvasToBlob(front));
        const back = await renderCardCanvas(person, ci, 'back');
        zip.file(`Namenskarten/${base}_name.png`, await canvasToBlob(back));
        if (!person.photo) missing.push(person.name || '(ohne Namen)');
        list.push(`${nr}  ${person.name || '(ohne Namen)'}${person.photo ? '' : '   – KEIN FOTO'}${person.photoRetouch ? '   – Handbearbeitung gewünscht' : ''}`);
        setDownloadingCards(`${i + 1}/${ordered.length}`);
      }

      // ── Extrakarten: freie Plätze in der Schachtel auffüllen ──
      const plan = extraPlanFor(ordered.length, familyColors);
      const { packs, capacity } = plan;
      const extraList = [];
      const safe = s => s.replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe').replace(/[üÜ]/g, 'ue').replace(/&/g, 'und').replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const leerCard = async (color) => renderCardCanvas({ id: 'leer', name: '', gender: null, photo: null }, { color }, 'front');
      for (let i = 0; i < plan.leerpersonen.length; i++) {
        const color = plan.leerpersonen[i];
        const nr = String(i + 1).padStart(2, '0');
        const canvas = await leerCard(color);
        const blob = await canvasToBlob(canvas);
        zip.file(`Leerpersonen/L${nr}_${color.replace('#', '')}_a.png`, blob);
        zip.file(`Leerpersonen/L${nr}_${color.replace('#', '')}_b.png`, blob);
        extraList.push(`L${nr}  Leerperson ${color} (2 Karten)`);
      }
      for (let i = 0; i < plan.extras.length; i++) {
        const card = plan.extras[i];
        const nr = String(i + 1).padStart(2, '0');
        const kat = card.kategorie !== null ? KATEGORIEN[card.kategorie] : '';
        const canvas = await renderExtraCardCanvas(card);
        zip.file(`Extrakarten/E${nr}_${safe(card.titel.replace('\n', ''))}.png`, await canvasToBlob(canvas));
        extraList.push(`E${nr}  ${card.titel.replace('-\n', '').replace('\n', ' ')}${kat ? '  (' + kat + ')' : ''}`);
      }
      for (let i = 0; i < plan.fuellkarten.length; i++) {
        zip.file(`Leerpersonen/F${String(i + 1).padStart(2, '0')}_leer.png`, await canvasToBlob(await leerCard(plan.fuellkarten[i])));
      }
      const extraKarten = plan.leerpersonen.length * 2 + plan.extras.length + plan.fuellkarten.length;

      zip.file('Uebersicht.txt',
        `Memo-Karten „${tree.name || tree.code}“ – ${ordered.length} Personen, ${ordered.length * 2} Karten\r\n` +
        `Format: ${CARD_CM} × ${CARD_CM} cm, ${CARD_DPI} dpi (${CARD_PX} × ${CARD_PX} Pixel), PNG mit transparenten Ecken\r\n` +
        (missing.length ? `\r\nACHTUNG – ohne Foto: ${missing.join(', ')}\r\n` : '') +
        `\r\n${list.join('\r\n')}\r\n` +
        `\r\nSchachtel: Platz für ${capacity} Personen (${packs.map(p => p.size + 'er').join(' + ')}), frei: ${plan.freiePlaetze}\r\n` +
        (extraKarten ? `Extrakarten: ${extraKarten} Karten – total ${ordered.length * 2 + extraKarten} Karten\r\n\r\n${extraList.join('\r\n')}\r\n` : 'Keine Extrakarten nötig.\r\n'));
      await saveZip(zip, `memo-karten_${tree.code}.zip`);
    } catch (err) {
      console.error(err);
      alert('Fehler beim Erstellen der Karten.');
    } finally {
      setDownloadingCards(null);
    }
  }, [persons, relationships, customColors, tree.code, tree.name]); // eslint-disable-line

  // ── Status ändern ─────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    if (newStatus === treeStatus) return;
    if (newStatus === 'erstellt') { setShowReturnDialog(true); return; } // zuerst Nachricht an den Kunden
    const labels = { erstellt: 'Entwurf', in_bearbeitung: 'In Bearbeitung', fertiggestellt: 'Fertiggestellt' };
    if (!window.confirm(`Status auf "${labels[newStatus]}" ändern?`)) return;
    await doStatusChange(newStatus, '');
  };
  const doStatusChange = async (newStatus, message) => {
    setChangingStatus(true);
    try {
      if (newStatus === 'erstellt') await doSave(); // letzte Änderungen speichern, danach kein Zugriff mehr
      const res = await fetch(`${api}/api/admin/tree/set-status`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ treeCode: tree.code, status: newStatus, message }),
      });
      if (res.status === 401) { onLogout(); return; }
      const d = await res.json();
      if (!res.ok) { alert(d.error || 'Fehler'); return; }
      if (newStatus === 'erstellt') {
        if (treeEmail && d.mailSent === false) alert('Zurückgegeben, aber die E-Mail an den Kunden konnte nicht gesendet werden.');
        isDirtyRef.current = false;
        onBack(); // ab jetzt gehört der Stammbaum wieder allein dem Kunden
        return;
      }
      setTreeStatus(newStatus);
    } catch {
      alert('Server nicht erreichbar');
    } finally {
      setChangingStatus(false);
    }
  };

  const editPerson     = persons.find(p => p.id === editPersonId)     || null;
  const selectedPerson = persons.find(p => p.id === selectedPersonId) || null;

  if (loadError) {
    return (
      <div className="admin-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#CC0000', fontSize: 16 }}>
          <p style={{ marginBottom: 16 }}>{loadError}</p>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onBack}>
            Zurück zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="admin-loading">Stammbaum wird geladen…</div>
      </div>
    );
  }

  return (
    <div className="admin-editor-layout">
      {showReturnDialog && (
        <ReturnDialog
          tree={tree}
          email={treeEmail}
          busy={changingStatus}
          onCancel={() => setShowReturnDialog(false)}
          onConfirm={msg => doStatusChange('erstellt', msg)}
        />
      )}
      {showRetouchList && (
        <div onClick={() => setShowRetouchList(false)} style={{
          position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: '22px 22px 18px', width: 'min(560px, 100%)',
            maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Bildbearbeitung von Hand ({retouchPersons.length} × 5 CHF)</h3>
              <button onClick={() => setShowRetouchList(false)} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            {retouchPersons.map(p => (
              <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                <img src={p.photo} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name || '(ohne Namen)'}</div>
                  <div style={{ fontSize: 13, color: p.photoRetouch.note ? '#333' : '#999', whiteSpace: 'pre-wrap' }}>
                    {p.photoRetouch.note || 'Keine besonderen Wünsche angegeben.'}
                  </div>
                </div>
                <button className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={() => { setShowRetouchList(false); setPhotoEditorState({ personId: p.id, photo: p.photo, edit: p.photoEdit || null, retouch: p.photoRetouch }); }}>
                  Öffnen
                </button>
              </div>
            ))}
            <p style={{ fontSize: 12, color: '#888', margin: '12px 0 0' }}>
              Beim Download „↓ Originalfotos“ liegen die Originale dieser Bilder zusätzlich im Ordner „Handbearbeitung“, zusammen mit einer Liste der Wünsche.
            </p>
          </div>
        </div>
      )}
      {showRemark && (
        <div onClick={() => setShowRemark(false)} style={{
          position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: '24px 24px 20px', width: 'min(520px, 100%)',
            maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Bemerkung des Kunden</h3>
              <button onClick={() => setShowRemark(false)} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            {orderData?.remark ? (
              <div style={{ whiteSpace: 'pre-wrap', background: '#fff8e6', borderLeft: '3px solid #f0a500', padding: '12px 14px', fontSize: 14, lineHeight: 1.5 }}>
                {orderData.remark}
              </div>
            ) : (
              <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Der Kunde hat keine Bemerkung hinterlassen.</p>
            )}
            {orderData && (
              <div style={{ marginTop: 16, fontSize: 13, color: '#666', lineHeight: 1.6 }}>
                {orderData.boxName && <div>Name auf der Spielebox: <strong>{orderData.boxName}</strong></div>}
                {orderData.name && <div>{orderData.name}</div>}
                {orderData.phone && <div>{orderData.phone}</div>}
                {orderData.copies && <div>{orderData.copies} {Number(orderData.copies) === 1 ? 'Exemplar' : 'Exemplare'}</div>}
              </div>
            )}
          </div>
        </div>
      )}
      <header className="admin-editor-header">
        <div className="admin-editor-brand">
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            style={{ marginRight: 4 }}
            onClick={async () => { await doSave(); onBack(); }}
          >
            ← Dashboard
          </button>
          <div>
            <div className="admin-editor-title">{tree.name || '(Kein Name)'}</div>
            <div className="admin-editor-email">
              {treeEmail}{orderData?.boxName ? <> · Spielebox: <strong>{orderData.boxName}</strong></> : null}
            </div>
          </div>
        </div>

        <div className="admin-editor-actions">
          {saveStatus === 'saving' && <span style={{ fontSize: 12, color: '#888' }}>↑ Speichern…</span>}
          {saveStatus === 'saved'  && <span style={{ fontSize: 12, color: '#09ad01' }}>✓ Gespeichert</span>}
          {saveStatus === 'error'  && <span style={{ fontSize: 12, color: '#cc0000' }}>✗ Fehler</span>}

          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => doSave(true)}
            disabled={saveStatus === 'saving'}
          >
            Speichern
          </button>

          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={handleDownloadAllPhotos}
            disabled={downloadingPhotos}
            title="Alle Originalfotos als ZIP herunterladen"
          >
            {downloadingPhotos ? 'Wird erstellt…' : '↓ Originalfotos'}
          </button>

          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={handleDownloadCards}
            disabled={!!downloadingCards || !persons.length}
            title={`Fertige Foto- und Namenskarten als PNG (${CARD_CM} × ${CARD_CM} cm, ${CARD_DPI} dpi)`}
          >
            {downloadingCards ? `Karten ${downloadingCards}…` : '↓ Memo-Karten'}
          </button>

          {retouchPersons.length > 0 && (
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => setShowRetouchList(true)}
              title="Fotos, die der Kunde von Hand bearbeiten lassen möchte"
              style={{ borderColor: '#c9a0f0', color: '#6b21a8', background: '#faf5ff' }}
            >
              ✋ Handbearbeitung ({retouchPersons.length})
            </button>
          )}

          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => setShowCardPreview(true)} disabled={!persons.length}>
            Vorschau
          </button>

          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => setShowAddModal(true)}>
            + Person
          </button>

          {orderData && (
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => setShowRemark(true)}
              title="Bemerkung des Kunden zur Bestellung"
              style={orderData.remark ? { borderColor: '#f0a500', color: '#8a4b00', background: '#fff8e6' } : undefined}
            >
              {orderData.remark ? '✎ Bemerkung' : 'Bemerkung'}
            </button>
          )}

          {treeStatus === 'in_bearbeitung' && (
            <button
              className="admin-btn admin-btn--finalize admin-btn--sm"
              onClick={handleFinalize}
              disabled={finalizing}
            >
              {finalizing ? 'Wird fertiggestellt…' : 'Fertigstellen'}
            </button>
          )}

          {treeStatus === 'fertiggestellt' && (
            <span className="admin-status-badge admin-status-badge--fertiggestellt">
              Fertiggestellt
            </span>
          )}

          {/* Status ändern */}
          <div style={{ position: 'relative' }}>
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => setShowStatusMenu(s => !s)}
              disabled={changingStatus}
            >
              {changingStatus ? 'Wird geändert…' : 'Status ändern ▾'}
            </button>
            {showStatusMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: '#fff', border: '1px solid #ddd', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 180,
              }}>
                {[
                  { value: 'erstellt', label: 'Entwurf (erstellt)' },
                  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
                  { value: 'fertiggestellt', label: 'Fertiggestellt' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleStatusChange(opt.value)}
                    style={{
                      display: 'block', width: '100%', padding: '10px 16px',
                      textAlign: 'left', background: treeStatus === opt.value ? '#f0f4ff' : 'transparent',
                      border: 'none', cursor: 'pointer', fontSize: 13,
                      fontWeight: treeStatus === opt.value ? 700 : 400,
                      color: treeStatus === opt.value ? '#1540dc' : '#333',
                    }}
                  >
                    {treeStatus === opt.value ? '✓ ' : '  '}{opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {finalizeError && (
            <span style={{ fontSize: 12, color: '#cc0000' }}>{finalizeError}</span>
          )}
        </div>
      </header>

      <main className="admin-editor-main" onClick={() => setSelectedPersonId(null)}>
        <TreeCanvas
          persons={persons}
          relationships={relationships}
          selectedId={selectedPersonId}
          onSelectPerson={id => setSelectedPersonId(prev => prev === id ? null : id)}
          onEditPerson={id => { setEditPersonId(id); setSelectedPersonId(null); }}
          onQuickAdd={handleQuickAdd}
          customColors={customColors}
          onColorChange={handleColorChange}
        />
        {selectedPerson && (
          <PersonDetailPanel
            person={selectedPerson}
            persons={persons}
            relationships={relationships}
            onEdit={id => { setEditPersonId(id); setSelectedPersonId(null); }}
            onDelete={id => { deletePerson(id); setSelectedPersonId(null); }}
            onEditPhoto={p => setPhotoEditorState({ personId: p.id, photo: p.photo, edit: p.photoEdit || null, retouch: p.photoRetouch })}
            onClose={() => setSelectedPersonId(null)}
          />
        )}
      </main>

      {showCardPreview && (
        <CardPreview
          persons={persons}
          relationships={relationships}
          customColors={customColors}
          canEdit
          onEditPhoto={p => setPhotoEditorState({ personId: p.id, photo: p.photo, edit: p.photoEdit || null, retouch: p.photoRetouch })}
          onAddPhoto={() => alert('Fotos fügt der Kunde hinzu. Du kannst bestehende Fotos zuschneiden und anpassen.')}
          onClose={() => setShowCardPreview(false)}
          showExtras
        />
      )}
      {photoEditorState && (
        <PhotoEditor
          photo={photoEditorState.photo}
          edit={photoEditorState.edit}
          title="Bild bearbeiten"
          retouch={photoEditorState.retouch}
          retouchMode="view"
          onCancel={() => setPhotoEditorState(null)}
          onConfirm={edit => { updatePerson(photoEditorState.personId, { photoEdit: edit }); setPhotoEditorState(null); }}
        />
      )}
      {showAddModal && (
        <AddPersonModal
          persons={persons}
          relationships={relationships}
          onClose={() => { setShowAddModal(false); setPresetRelation(null); }}
          onSave={handleAddSave}
          presetRelation={presetRelation}
        />
      )}

      {editPersonId && editPerson && (
        <AddPersonModal
          persons={persons}
          relationships={relationships}
          editPerson={editPerson}
          onClose={() => setEditPersonId(null)}
          onSave={handleEditSave}
        />
      )}

      {showLimitModal && <LimitModal reason={showLimitModal} onClose={() => setShowLimitModal(null)} />}
    </div>
  );
}
